"use client";

import React, { useEffect, useRef, useState } from "react";
import { ExternalLink, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const locations = [{
  id: "loc-main",
  title: "The Wedding Ceremony",
  addressLine1: "St. Mary's Cathedral",
  addressLine2: "209 Elizabeth St, Sydney NSW 2000",
  mapLink: "#"
}, {
  id: "loc-partner",
  title: "The Evening Reception",
  addressLine1: "The Heritage Ballroom",
  addressLine2: "45 George Street, The Rocks NSW 2000",
  mapLink: "#"
}];
function LocationCard({
  location,
  index
}: {
  location: (typeof locations)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={cn("transition-all duration-700 ease-out h-full", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="h-full flex flex-col shadow-md hover:shadow-lg transition-shadow duration-300 border-border/30 bg-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-default text-3xl text-foreground italic">
            {location.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-grow">
          <div className="flex items-start gap-3 text-foreground">
            <MapPin className="w-5 h-5 shrink-0 mt-0.5 text-primary" />
            <div className="flex flex-col gap-1">
              <span className="font-default font-bold text-lg tracking-wide">{location.addressLine1}</span>
              <span className="font-sans text-sm tracking-wider uppercase opacity-80">{location.addressLine2}</span>
            </div>
          </div>
        </CardContent>
        <CardFooter className="pt-4">
          <Button asChild variant="outline" className="w-full sm:w-auto group border-primary/20 hover:bg-secondary rounded-md">
            <Link to={location.mapLink} newTab>
              View Location
              <ExternalLink className="w-4 h-4 ml-2 opacity-70 group-hover:opacity-100 transition-opacity" />
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </div>;
}
export default function ProcedureCenters() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="253938" id="the-celebration-logistics" className="bg-background text-foreground py-20 md:py-28">
      <div className="max-w-screen-lg mx-auto px-6">
        <div ref={headerRef} className={cn("text-center mb-16 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-6 tracking-tight">
            The Celebration Logistics
          </h2>
          <div className="w-16 h-px bg-secondary mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {locations.map((location, index) => <LocationCard key={location.id} location={location} index={index} />)}
        </div>
      </div>
    </section>;
}